import { Component, OnInit, ViewChild } from '@angular/core';
import {
  DataSourceParams,
  EFilterSize,
  EFilterType,
  ETableColumnType,
  ITableColumn,
  ITableFIlterGroup,
} from '../../store/models/table.model';
import { IContactItem, IMoveContactUploadItem } from '../../store/models/contact.model';
import { ApiDataSource } from '../../datasources/api.datasrouce';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { SelectionChange } from '@angular/cdk/collections';
import { ButtonColor, ButtonSize } from '../../store/models/button.model';
import { AccountService } from '../../store/services/account.service';
import { IAccount } from '../../store/models/account.model';
import { UserType } from '../../store/models/user.model';
import { ContactsService, IContactMoveResponse } from '../../store/services/contacts.service';
import { map } from 'rxjs/operators';
import { TableComponent } from '../../shared/ui/table/table.component';
import { Store } from '@ngrx/store';
import { IReducers } from '../../store/reducers';
import { openDialog, openSnackbar } from '../../store/actions/page.action';
import { BatchMoveDialogComponent } from './batch-move-dialog/batch-move-dialog.component';
import { EUploadState, UploadDialogComponent } from './upload-dialog/upload-dialog.component';
import { FileUploaderService } from '../../store/services/file-uploader.service';
import { UploadConfirmationDialogComponent } from './upload-confirmation-dialog/upload-confirmation-dialog.component';

@Component({
  selector: 'app-move-contacts',
  templateUrl: './move-contacts.component.html',
  styleUrls: ['./move-contacts.component.scss'],
})
export class MoveContactsComponent implements OnInit {
  @ViewChild('tableComponent') tableComponent: TableComponent;

  tableColumns: ITableColumn<IContactItem>[] = [
    {
      id: 'name',
      title: 'Name',
      renderer: (contact: IContactItem) =>
        `${contact.contact_firstname} ${contact.contact_lastname}`,
    },
    {
      id: 'contact_email',
      title: 'Email',
    },
    {
      id: 'account_name',
      title: 'Account',
      renderer: (contact: IContactItem) =>
        `${contact.account_firstname} ${contact.account_lastname}`,
    },
  ];

  dataSource: ApiDataSource<IContactItem>;

  tableDataSourceParams: DataSourceParams = {
    page: 1,
    pageSize: 10,
    sortAttr: 'id',
    sortDir: 'asc',
    filters: {},
  };

  tableFilterGroups: ITableFIlterGroup[] = [
    {
      filters: [
        {
          type: EFilterType.SEARCH,
          size: EFilterSize.HALF,
          items: [
            {
              id: 'search',
              label: 'Search',
              icon: 'search',
            },
          ],
          onFilterChange: value => {
            this.searchChange(value);
          },
        },
        {
          type: EFilterType.BUTTON,
          label: 'Upload',
          buttonProperties: {
            size: ButtonSize.SMALL,
            color: ButtonColor.BLUE,
            text: 'Upload',
            // onClick: () => this.fileUploaderService.selectFile()
            onClick: () =>
              this.store$.dispatch(
                openDialog({
                  dialogData: {
                    content: UploadDialogComponent,
                    componentData: {
                      state: EUploadState.SELECT_TEMPLATE,
                    },
                    dialogWidth: '500px',
                    title: 'Select Upload Template',
                    buttons: [
                      {
                        text: 'Next',
                        size: ButtonSize.NORMAL,
                        color: ButtonColor.BLUE,
                        onClick: dialog => {
                          if (!dialog.dialogForm.valid) {
                            dialog.dialogForm.markAllAsTouched();
                            return;
                          }

                          const selectedTemplate =
                            dialog.dialogForm.get('template').value;
                          dialog.closeDialog();
                          this.fileUploaderService.init({
                            accept: ['text/csv', '.xls', '.xlsx'],
                            buttonLabel: 'Select Files',
                            dialogTitle: 'Upload file',
                            requestUri:
                              environment.apiUrl + '/v1/contacts/upload-move',
                            showDropZone: true,
                            allowMultiple: false,
                            param: 'contacts_file',
                            maxFiles: 1,
                            maxFileSize: '4MB',
                            extraParams: {
                              template: selectedTemplate,
                            },
                            onComplete: (response: IMoveContactUploadItem) => {
                              this.store$.dispatch(
                                openDialog({
                                  dialogData: {
                                    content: UploadConfirmationDialogComponent,
                                    componentData: {
                                      items: response,
                                    },
                                    dialogWidth: '900px',
                                    title: 'Upload Status',
                                    canClose: true,
                                  },
                                }),
                              );
                            },
                            onError: res =>
                              this.store$.dispatch(
                                openSnackbar({
                                  snackbarData: {
                                    data: {
                                      message: res.error,
                                      type: 'error',
                                    },
                                  },
                                }),
                              ),
                          });
                          this.fileUploaderService.selectFile();
                        },
                      },
                    ],
                  },
                }),
              ),
          },
        },
      ],
    },
  ];

  accounts: IAccount[] = [];

  showActions: boolean;

  selectedContacts: IContactItem[] = [];

  tableReady: boolean;

  private multipleContactMoveResponse: IContactMoveResponse;

  constructor(
    private readonly http: HttpClient,
    private readonly accountService: AccountService,
    private readonly contactsService: ContactsService,
    private readonly store$: Store<IReducers>,
    private readonly fileUploaderService: FileUploaderService,
  ) {
    this.dataSource = new ApiDataSource<IContactItem>(
      this.http,
      environment.apiUrl + '/v1/contacts',
    );
  }

  searchChange(value) {
    this.tableDataSourceParams.filters.search = value;
  }

  ngOnInit(): void {
    this.accountService
      .getAccountsBasicInfo(UserType.SINGLE_USER)
      .subscribe(result => {
        this.accounts = result.items;
        this.tableColumns.push({
          id: 'account_id',
          title: 'Destination Account',
          type: ETableColumnType.SELECT,
          selectProperties: {
            type: 'select',
            label: 'Destination Account',
            items: this.accounts.map(account => ({
              label: `${account.first_name} ${account.last_name}`,
              value: account.account_id,
            })),
            onSelection: (value, tableComponent, element) => {
              const data = [
                ...this.dataSource.data.map(contact => {
                  if (contact.contact_id === element.contact_id) {
                    contact.account_id = value;
                    contact.group_id = null;
                  }

                  return contact;
                }),
              ];

              this.dataSource.internalRefresh(data);
            },
          },
        });

        this.tableColumns.push({
          id: 'group_id',
          title: 'Destination Group',
          type: ETableColumnType.SELECT,
          selectProperties: {
            type: 'select',
            label: 'Destination Group',
            asyncBuildItems: true,
            trackBy: 'account_id',
            buildItems: (row: IContactItem) => {
              return this.contactsService.getContactGroups(row.account_id).pipe(
                map(groups =>
                  groups.map(g => ({
                    label: g.group_name,
                    value: g.group_id,
                  })),
                ),
              );
            },
            onSelection: (value, tableComponent, element) => {
              const data = [
                ...this.dataSource.data.map(contact => {
                  if (contact.contact_id === element.contact_id) {
                    contact.group_id = value;
                  }

                  return contact;
                }),
              ];

              this.dataSource.internalRefresh(data);
            },
          },
        });

        this.tableColumns.push({
          id: 'status',
          title: 'Status',
          renderer: (contact: IContactItem) => {
            if (!this.multipleContactMoveResponse) {
              return '';
            }

            const taragetMoved = this.multipleContactMoveResponse.moved.filter(
              m => m.contact_id === contact.contact_id,
            );

            if (taragetMoved && taragetMoved.length === 1) {
              return taragetMoved[0].message;
            }

            const targetFailed = this.multipleContactMoveResponse.failed.filter(
              m => m.contact_id === contact.contact_id,
            );

            if (targetFailed && targetFailed.length === 1) {
              return targetFailed[0].message;
            }
          },
        });

        this.tableReady = true;
      });
  }

  onSelection($event: SelectionChange<IContactItem>) {
    this.showActions = $event.source.selected.length > 0;
    this.selectedContacts = $event.source.selected;

    if (this.showActions) {
      if (this.tableFilterGroups[0].filters.length === 2) {
        this.tableFilterGroups[0].filters.push({
          type: EFilterType.BUTTON,
          label: 'Create new',

          buttonProperties: {
            size: ButtonSize.SMALL,
            color: ButtonColor.ORANGE,
            text: 'Move',
            onClick: () =>
              this.store$.dispatch(
                openDialog({
                  dialogData: {
                    content: 'Are you sure you want to move selected contacts?',
                    dialogWidth: '500px',
                    title: 'Move Contacts',
                    buttons: [
                      {
                        text: 'Move',
                        size: ButtonSize.NORMAL,
                        color: ButtonColor.BLUE,
                        onClick: dialog => {
                          const wrongSelected = this.selectedContacts.filter(
                            c => c.account_id === null,
                          );

                          if (wrongSelected && wrongSelected.length) {
                            alert(
                              'Please select destination account and group for all contacts',
                            );
                            return;
                          }

                          const paramsMap: {
                            contact_id: number[];
                            account_id: number[];
                            group_id: number[];
                          } = {
                            contact_id: [],
                            account_id: [],
                            group_id: [],
                          };

                          this.selectedContacts.forEach(c => {
                            paramsMap.contact_id.push(c.contact_id);
                            paramsMap.account_id.push(c.account_id);
                            // group_id is optional
                            if (c.group_id) {
                              paramsMap.group_id.push(c.group_id);
                            }
                          });

                          if (
                            (paramsMap.contact_id.length +
                              paramsMap.account_id.length) /
                            2 !==
                            paramsMap.contact_id.length
                          ) {
                            alert(
                              'Please select destination account and group for all contacts',
                            );
                            return;
                          }

                          this.contactsService
                            .moveMultipleContacts(paramsMap)
                            .subscribe(
                              result => {
                                this.multipleContactMoveResponse = result;
                                this.dataSource.refresh();
                                this.store$.dispatch(
                                  openSnackbar({
                                    snackbarData: {
                                      data: {
                                        message:
                                          'Contacts moved successfully. Please, check the status column for more details.',
                                        type: 'success',
                                      },
                                    },
                                  }),
                                );
                                dialog.closeDialog();
                              },
                              error =>
                                this.store$.dispatch(
                                  openSnackbar({
                                    snackbarData: {
                                      data: {
                                        message: error.error,
                                        type: 'error',
                                      },
                                    },
                                  }),
                                ),
                            );
                        },
                      },
                    ],
                  },
                }),
              ),
          },
        });
        this.tableFilterGroups[0].filters.push({
          type: EFilterType.BUTTON,
          label: 'Create new',

          buttonProperties: {
            size: ButtonSize.SMALL,
            color: ButtonColor.BLUE,
            text: 'Batch Move',
            onClick: (row: IContactItem) =>
              this.store$.dispatch(
                openDialog({
                  dialogData: {
                    content: BatchMoveDialogComponent,
                    componentData: {
                      accounts: this.accounts,
                    },
                    canClose: true,
                    dialogWidth: '700px',
                    title: 'Batch Move Contacts',
                    buttons: [
                      {
                        text: 'Move',
                        size: ButtonSize.NORMAL,
                        color: ButtonColor.BLUE,
                        onClick: dialog => {
                          if (!dialog.dialogForm.valid) {
                            dialog.dialogForm.markAllAsTouched();
                            return;
                          }

                          const selectedContactIds = this.selectedContacts.map(
                            c => c.contact_id,
                          );
                          const targetAccountId =
                            dialog.dialogForm.get('accountId').value;
                          const targetGroupId =
                            dialog.dialogForm.get('groupId').value;

                          this.contactsService
                            .moveMultipleContacts({
                              contact_id: selectedContactIds,
                              account_id: [targetAccountId],
                              group_id: [targetGroupId],
                            })
                            .subscribe(
                              result => {
                                this.multipleContactMoveResponse = result;
                                this.dataSource.refresh();
                                dialog.closeDialog();
                                this.store$.dispatch(
                                  openSnackbar({
                                    snackbarData: {
                                      data: {
                                        message:
                                          'Contacts moved successfully. Please, check the status column for more details.',
                                        type: 'success',
                                      },
                                    },
                                  }),
                                );
                              },
                              error =>
                                this.store$.dispatch(
                                  openSnackbar({
                                    snackbarData: {
                                      data: {
                                        message: error.error,
                                        type: 'error',
                                      },
                                    },
                                  }),
                                ),
                            );
                        },
                      },
                    ],
                  },
                }),
              ),
          },
        });
      }
    } else {
      this.tableFilterGroups[0].filters.splice(2, 2);
    }
  }
}
